// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable, { ClickableLinkProps } from "@insite/mobius/Clickable";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { HasStepperContext, StepperContext, withStepperContext } from "@rpsc/App/Widgets/Stepper/StepperContext";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import { mergeWithCss } from "@rpsc/XDL/Utilities";
import React, { useContext, useState } from "react";

interface OwnProps {
    extendedStyles?: StepperStyles;
}

type Props = OwnProps & HasStepperContext;

export interface StepperStyles {
    container?: InjectableCss;
    stepWrapper?: InjectableCss;
    stepNumber?: TypographyProps;
    stepClickableWrapper?: ClickableLinkProps;
    stepName?: TypographyProps;
}

export const stepperStyles: StepperStyles = {};

export const Stepper: React.FC<Props> = props => {
    const { steps, currentStep, allowStepClick, moveToStepHandler } = useContext(StepperContext);
    const [styles] = useState(() => mergeWithCss(stepperStyles, props.extendedStyles));

    const StepperComponent = () => {
        return (
            <StyledWrapper {...styles.container} className="stepper" aria-label="Stepper">
                {steps.map(step => {
                    const isCompleted = step.index < currentStep;
                    const isActive = step.index === currentStep;
                    const stepStatus = isActive ? "active" : isCompleted ? "complete" : "incomplete";

                    return (
                        <>
                            <StyledWrapper key={step.index} className={stepStatus} {...styles.stepWrapper}>
                                <Clickable
                                    onClick={() => isCompleted && allowStepClick && moveToStepHandler(step.index)}
                                    aria-label={`Stepper_${step.index}_Clickable`}
                                    {...styles.stepClickableWrapper}
                                >
                                    <Typography
                                        {...styles.stepNumber}
                                        data-test-selector={`stepNumber_${1}`}
                                        className={stepStatus}
                                    >
                                        {step.index}
                                    </Typography>
                                    <Typography {...styles.stepName}>{step.displayName}</Typography>
                                </Clickable>
                            </StyledWrapper>
                        </>
                    );
                })}
            </StyledWrapper>
        );
    };

    return <StepperComponent />;
};

export const StepperComponent = withStepperContext(Stepper);

const widgetModule: WidgetModule = {
    component: StepperComponent,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        displayName: "Stepper",
        fieldDefinitions: [],
    },
};

export default widgetModule;
